import {ActivityIndicator, Alert, StyleSheet, Text, View} from "react-native";
import {EventHappening, GroupFollowRequest, Membership} from "@/lib/types";
import {useRouter} from "expo-router";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import Card from "@/lib/components/Card";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {Color} from "@/lib/styles";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import AvatarBox from "@/lib/components/AvatarBox";
import Header from "@/lib/components/Header";
import NavButton from "@/lib/components/NavButton";
import {useEffect, useMemo, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {useActivity} from "@/lib/components/ActivityProvider";
import {useGroup} from "@/lib/components/GroupProvider";
import PrimaryText from "@/lib/components/PrimaryText";
import {useQuery} from "@tanstack/react-query";
import EventCard from "@/lib/components/EventCard";

export default function GroupPage() {
    const {session} = useSession();
    const router = useRouter();
    const [busy, setBusy] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const {refreshActivities} = useActivity();

    const {group, groupLoading, groupError, refreshGroup} = useGroup();

    const members = useMemo(() => {
        if (!group) {
            return [];
        }
        return group.members.filter(m => (
            m.status === Membership.ADMINISTRATOR ||
            m.status === Membership.MODERATOR ||
            m.status === Membership.MEMBER
        ));
    }, [group]);

    const numInvited = useMemo(() => group?.members.filter(p => p.status === Membership.INVITED).length || 0, [group]);

    const infoLines = useMemo(() => {
        if (!group || !group.info) {
            return [];
        }
        return group.info.split(/\n\n+/);
    }, [group])

    // clear notifications for this group when loaded
    useEffect(() => {
        if (!group) {
            return;
        }
        refreshActivities();
    }, [group, refreshActivities]);

    const eventQuery = useQuery<EventHappening[]>({
        queryKey: ['group', group?.group_id, 'events', session],
        queryFn: () => fetchBackend(`/group/${group?.group_id}/events`, "GET", session),
        retry: dataRetryPolicy,
        enabled: !groupLoading && !groupError && !!group,
    })

    if (groupError) {
        return <ErrorPage error={groupError}/>
    } else if (groupLoading || !group) {
        return <SpinnerPage/>
    }

    async function saveAttendance(request: GroupFollowRequest) {
        if (!group) {
            return;
        }
        setBusy(true);
        setSaveError(null);
        fetchBackendNoContent(`/group/${group.group_id}/join`, "POST", session, request).then(async () => {
            refreshGroup();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    const canJoin = (group.membership_status === Membership.NOTMEMBER || group.membership_status === Membership.INVITED);
    const canLeave = (group.membership_status === Membership.MEMBER || group.membership_status === Membership.MODERATOR);
    const isAdmin = (group.membership_status === Membership.ADMINISTRATOR);

    return (
        <Page scrollable={true}>
            {group.invited_by && group.membership_status === Membership.INVITED &&
                <AvatarBox size={48} border={false} avatars={[group.invited_by]} color={group.color}>
                    <Text style={styles.invitedByText}>{group.invited_by.username} har invitert deg</Text>
                </AvatarBox>
            }

            <Card image_url={group.image_url}
                  color={group.color}
                  contrast_color={group.contrast_color}
                  title={group.name}
            >
                {group.public && <PrimaryText>Offentlig samfunn</PrimaryText>}
                {!group.public && <PrimaryText>Privat samfunn</PrimaryText>}
                {session &&
                    <Nav style={{flexDirection: "row-reverse", justifyContent: "flex-start"}}>
                        {canLeave && <ActionButton
                            title={"Du er medlem av samfunnet"}
                            color={Color.Green}
                            busy={busy}
                            disabled={busy}
                            icon={"checkmark"}
                            onPress={async () => {
                                await saveAttendance({want_membership: false});
                            }}
                        />}
                        {canJoin && <ActionButton
                            title={"Bli med i samfunnet"}
                            color={Color.Green}
                            busy={busy}
                            disabled={busy}
                            activated={true}
                            onPress={async () => {
                                await saveAttendance({want_membership: true});
                            }}
                        />}
                        {isAdmin && <ActionButton
                            title={"Du er administrator"}
                            color={Color.Green}
                            icon={"checkmark"}
                            activated={true}
                            onPress={() => {
                                Alert.alert(
                                    "Utvidede rettigheter",
                                    "Fordi du er administrator kan du ikke melde deg ut av gruppen på egen hånd. Be en annen administrator fjerne din tilgang før du melder deg ut.",
                                );
                            }}
                        />}

                    </Nav>
                }

            </Card>

            {
                saveError && <ErrorMessage error={saveError}/>
            }

            {
                !!session &&
                <>
                    <Header title={`Medlemmer ( ${members.length} )`} marginTop={6}/>
                    <AvatarBox avatars={members.map(member => member.avatar)}
                               color={group.color}
                               numItems={7}
                               onPress={() => {
                                   router.navigate(`/s/${group.group_id}/members`);
                               }}
                    >
                        <View style={styles.extraInvited}>
                            {numInvited > 0 && <Text style={styles.extraInvitedText}>+ {numInvited} invitert</Text>}
                        </View>
                    </AvatarBox>
                </>
            }
            {
                !session &&
                <NavButton
                    title={"Logg inn for å delta i samfunnet"}
                    href={`/u/account/login`}
                    icon={"relation"}
                />
            }
            <View style={styles.eventContainer}>
                <Header
                    icon={"event"}
                    title={eventQuery.data && eventQuery.data.length > 0 ? `Eventer ( ${eventQuery.data.length} )` : "Eventer"}
                />
                {eventQuery.data && eventQuery.data.length === 0 &&
                    <PrimaryText>Samfunnet har ingen aktive eventer.</PrimaryText>}
                {eventQuery.data && eventQuery.data.map(e => <EventCard event={e} key={e.event_id}/>)}
                {eventQuery.isLoading && <ActivityIndicator color={Color.PrimaryText} size={"small"}/>}
                {eventQuery.error && <ErrorMessage error={eventQuery.error}/>}
            </View>
            {
                group.info ?
                    <View style={styles.infoContainer}>
                        <Header title={"Info om samfunnet"}/>
                        {infoLines.map((line, key) =>
                            <Text style={styles.infoText}
                                  key={key}
                                  lineBreakStrategyIOS={"push-out"} // unngå horunger
                                  selectable={true}
                            >{line}</Text>
                        )}
                    </View>
                    :
                    <View style={styles.infoContainer}>
                        <Header title={"Samfunnet har ingen ytterligere beskrivelse."}/>
                    </View>
            }
        </Page>
    )
}

export const styles = StyleSheet.create({
    infoContainer: {
        width: "100%",
        marginTop: 12,
    },
    eventContainer: {
        marginTop: 12,
        gap: 8,
    },
    infoText: {
        color: Color.PrimaryText,
        marginTop: 8,
        fontSize: 18,
        lineHeight: 26,
        paddingRight: 16,
    },
    extraInvited: {
        flexDirection: "column",
        flexWrap: "nowrap",
        gap: 4,
        justifyContent: "center",
    },
    extraInvitedText: {
        color: Color.PrimaryText,
        fontSize: 12,
        lineHeight: 12,
        fontWeight: 500,
    },
    invitedByText: {
        color: Color.PrimaryText,
        fontWeight: 500,
        fontSize: 18,
    },
    arrangedByText: {
        color: Color.PrimaryText,
        fontSize: 16,
    }
})